import { useEffect, useState } from 'react'
import { Plus, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSaveSizeNorm, useSizeNorm } from '@/hooks/useSizeNorm'
import type { SizeNormRow } from '@/lib/api/session11Types'

type EditRow = SizeNormRow & { _new?: boolean }

// Stable fallback: a fresh `= []` each render re-fired the sync effect below
// on every render while loading — an endless render loop.
const NO_ROWS: SizeNormRow[] = []

export function SizeNormSection() {
  const { data: server = NO_ROWS, isLoading } = useSizeNorm()
  const save = useSaveSizeNorm()
  const [rows, setRows] = useState<EditRow[]>([])

  useEffect(() => {
    setRows(server.map((r) => ({ ...r })))
  }, [server])

  function addRow() {
    setRows((prev) => [...prev, { raw_variant: '', normalized_tier: '', schema_type: '', _new: true }])
  }

  function update(idx: number, patch: Partial<EditRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  function remove(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx))
  }

  async function onSave() {
    const cleaned = rows.filter((r) => r.raw_variant.trim() && r.normalized_tier.trim())
    if (cleaned.length !== rows.length) {
      toast.warning(`Skipping ${rows.length - cleaned.length} row(s) with empty raw_variant/normalized_tier`)
    }
    // dedupe — backend rejects duplicates
    const seen = new Set<string>()
    for (const r of cleaned) {
      if (seen.has(r.raw_variant)) {
        toast.error(`Duplicate raw_variant '${r.raw_variant}'`)
        return
      }
      seen.add(r.raw_variant)
    }
    try {
      await save.mutateAsync(cleaned.map(({ _new, ...r }) => r))
      toast.success(`Saved ${cleaned.length} size mapping(s)`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'save failed'
      toast.error(msg)
    }
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">Size Normalization</h2>
        <span className="text-xs text-[var(--mute)]">{rows.length} mappings</span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addRow}>
          <Plus className="mr-1 h-3 w-3" />
          Add Row
        </Button>
        <Button size="sm" className="h-7 text-xs" onClick={onSave} disabled={save.isPending}>
          <Save className="mr-1 h-3 w-3" />
          Save
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-1">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}</div>
      ) : (
        <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)] max-h-96">
          <table className="text-xs w-full">
            <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)] sticky top-0">
              <tr className="text-left text-[var(--mute)]">
                <th className="tbl-th">Raw Variant</th>
                <th className="tbl-th">Normalized Tier</th>
                <th className="tbl-th">Schema Type</th>
                <th className="tbl-th w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={`${r.raw_variant}-${idx}`}
                  className={`border-b border-[var(--line-warm)] ${
                    idx % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                  }`}
                >
                  <td className="px-1 py-0.5">
                    {r._new ? (
                      <input value={r.raw_variant} onChange={(e) => update(idx, { raw_variant: e.target.value })} className="w-full px-1 py-0.5 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]" />
                    ) : <span className="px-1">{r.raw_variant}</span>}
                  </td>
                  <td className="px-1 py-0.5">
                    <input value={r.normalized_tier} onChange={(e) => update(idx, { normalized_tier: e.target.value })} className="w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] focus:outline-none focus:border-[var(--brand)] bg-transparent" />
                  </td>
                  <td className="px-1 py-0.5">
                    <input value={r.schema_type} onChange={(e) => update(idx, { schema_type: e.target.value })} className="w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] focus:outline-none focus:border-[var(--brand)] bg-transparent" />
                  </td>
                  <td className="px-1 py-0.5">
                    <button aria-label="Remove row" onClick={() => remove(idx)} className="p-1 hover:text-destructive">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
